import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, ArrowRight, CheckCircle, Copy, Loader2, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import AudioQrCard from '../components/AudioQrCard';
import AffirmationAudioRecorder from '../components/AffirmationAudioRecorder';
import { createAudioToken, getAudioSubmission } from '../lib/api';
import { createAffirmationScript, getAudioStatusLabel, normalizePhrases } from '../lib/affirmationAudio';

export function StepAudioOptional({
  submissionId,
  sessionId,
  phrases,
  audioToken,
  audioUrl,
  audioStatus,
  onAudioTokenChange,
  onAudioUrlChange,
  onAudioStatusChange,
  onSaveForLater,
  onNext,
  onBack,
}) {
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSavingForLater, setIsSavingForLater] = useState(false);
  const [showMissingAudioDialog, setShowMissingAudioDialog] = useState(false);
  const [savedForLater, setSavedForLater] = useState(false);
  const cleanPhrases = useMemo(() => normalizePhrases(phrases).filter(Boolean), [phrases]);
  const scriptText = useMemo(() => createAffirmationScript(cleanPhrases), [cleanPhrases]);
  const hasUploaded = audioStatus === 'recording_uploaded' || audioStatus === 'submitted_to_editor';
  const isWaitingForMobileUpload = Boolean(audioToken && audioUrl && !hasUploaded);
  const autoAdvancedRef = useRef(false);

  const ensureAudioLink = useCallback(async () => {
    if (audioToken && audioUrl) return { token: audioToken, audio_url: audioUrl };
    if (!submissionId) throw new Error('Falta guardar el formulario en el servidor.');
    setIsPreparing(true);
    try {
      const result = await createAudioToken(submissionId, sessionId);
      onAudioTokenChange(result.token);
      onAudioUrlChange(result.audio_url);
      return result;
    } finally {
      setIsPreparing(false);
    }
  }, [audioToken, audioUrl, onAudioTokenChange, onAudioUrlChange, sessionId, submissionId]);

  const handleCopyScript = async () => {
    try {
      await navigator.clipboard.writeText(scriptText);
      toast.success('Texto copiado');
    } catch (error) {
      toast.error('No se pudo copiar el texto');
    }
  };

  const handleSaveForLater = async () => {
    setIsSavingForLater(true);
    try {
      await ensureAudioLink();
      const saved = await onSaveForLater?.();
      if (saved === false) return;
      setSavedForLater(true);
      toast.success('Formulario guardado. Podés volver después para cargar el audio.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || 'No se pudo guardar para continuar después');
    } finally {
      setIsSavingForLater(false);
    }
  };

  const handleFinalize = () => {
    if (!hasUploaded) {
      setShowMissingAudioDialog(true);
      return;
    }
    onNext();
  };

  const advanceAfterUpload = useCallback((status) => {
    if (status !== 'recording_uploaded' && status !== 'submitted_to_editor') return;
    if (autoAdvancedRef.current) return;
    autoAdvancedRef.current = true;
    onAudioStatusChange(status);
    toast.success('Audio enviado. Avanzamos al resumen.');
    onNext();
  }, [onAudioStatusChange, onNext]);

  const handleUploaded = (result) => {
    advanceAfterUpload(result.status);
  };

  useEffect(() => {
    if (!audioToken || autoAdvancedRef.current) return undefined;

    let cancelled = false;
    const pollAudioStatus = async () => {
      try {
        const result = await getAudioSubmission(audioToken);
        if (cancelled) return;
        if (result.status && result.status !== audioStatus) {
          onAudioStatusChange(result.status);
        }
        advanceAfterUpload(result.status);
      } catch (error) {
        // The token can briefly be unavailable while the link is being prepared.
      }
    };

    pollAudioStatus();
    const intervalId = window.setInterval(pollAudioStatus, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [advanceAfterUpload, audioStatus, audioToken, onAudioStatusChange]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle>Audio opcional</CardTitle>
              <CardDescription>
                Ya tenés el texto listo. Podés grabarlo ahora, abrirlo desde el celular o guardar para continuar más tarde.
              </CardDescription>
            </div>
            <Badge variant="outline">{getAudioStatusLabel(audioStatus)}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-md border bg-card p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-primary" />
                Texto para grabar
              </h3>
              <Button type="button" variant="outline" size="sm" onClick={handleCopyScript}>
                <Copy className="w-4 h-4 mr-2" />
                Copiar
              </Button>
            </div>
            <div className="whitespace-pre-line text-sm leading-6">
              {scriptText}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={ensureAudioLink} disabled={isPreparing || !submissionId}>
              {isPreparing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Preparar link / QR
            </Button>
          </div>

          {audioUrl && <AudioQrCard audioUrl={audioUrl} />}

          {isWaitingForMobileUpload && (
            <div className="rounded-md border border-cyan-200 bg-cyan-50/70 p-4 text-sm text-cyan-900 dark:bg-cyan-950/20 dark:border-cyan-900 dark:text-cyan-100">
              <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
              Esperando audio desde el celular. Esta pantalla avanza sola cuando se envía.
            </div>
          )}

          {savedForLater && !hasUploaded && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/20 dark:border-amber-900 dark:text-amber-100">
              Quedó guardado con audio pendiente. Podés cerrar esta pantalla y volver al formulario con el mismo email y contraseña para continuar desde acá.
            </div>
          )}

          <AffirmationAudioRecorder
            token={audioToken}
            disabled={!audioToken || isPreparing}
            scriptText={scriptText}
            onUploaded={handleUploaded}
          />

          {hasUploaded && (
            <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:bg-green-950/20 dark:border-green-900 dark:text-green-100">
              <CheckCircle className="w-4 h-4 inline mr-2" />
              Audio cargado. Si esta pantalla no avanzó automáticamente, podés finalizar desde el botón de abajo.
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onBack} disabled={isSavingForLater}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="button" variant="outline" onClick={handleSaveForLater} disabled={isSavingForLater || isPreparing}>
                {isSavingForLater ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Guardar y continuar más tarde
              </Button>
              <Button type="button" onClick={handleFinalize} disabled={isSavingForLater}>
                Finalizar formulario
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
          {!hasUploaded && (
            <p className="text-xs text-muted-foreground text-right">
              Para finalizar ahora grabá o seleccioná tu audio y presioná “Subir audio”. Si necesitás hacerlo después, guardá y continuá más tarde.
            </p>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showMissingAudioDialog} onOpenChange={setShowMissingAudioDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Falta grabar o subir el audio</AlertDialogTitle>
            <AlertDialogDescription>
              Para finalizar este paso necesitamos que grabes o selecciones tu audio y después presiones “Subir audio”. Si no podés hacerlo ahora, usá “Guardar y continuar más tarde” para retomarlo después con el mismo email y contraseña.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Entendido</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default StepAudioOptional;
